/**
 * Pages parc véhicules — Phase 2A étendue L13.
 *
 * Exporte :
 *   - ParcDashboardPage      : Desktop. 4 KPI cards + tableau véhicules avec alertes colorées
 *   - VehiculeDetailPage     : Fiche véhicule + carnet d'entretien + accès scan facture
 *   - VehiculeFormPage       : Création / édition véhicule
 *   - ScanFactureMobilePage  : Mobile-first. Capture caméra arrière → OCR → validation
 *
 * Refonte R8 (mai 2026) :
 *   - Topbar glass cohérent avec halo tech (au lieu de bg-tech plein)
 *   - Sub-nav pills glass (cohérent R6.2 service-technique : Signalements / Planning / Arrêtés / Parc actif)
 *   - LogoMark Truck accent tech
 *   - ParcKpi en cards glass avec halo radial du tone (warning/urgent/success/neutral) — purge-safe
 *   - Tableau véhicules avec rows hover glass + halo urgent/warning si alerte
 *   - Carnet d'entretien en cards glass avec icône intervention dans rounded-r-lg tech
 *   - ScanFactureMobilePage mobile-first avec header glass + dropzone bento + OCR overlay
 *
 * Logique métier 100% préservée :
 *   - api.parc.dashboard/get/create/update/delete + api.parc.carnet.create/delete + api.parc.scanFacture
 *   - Maps VEHICULE_TYPE_LABEL, TYPE_INTER_LABEL, TYPE_INTER_ICON
 *   - canCreate / canEdit (manager+) · canDelete (admin+)
 *   - Alertes calculées : alertPneus (rouge), alertCt (orange), alertVidange (orange)
 *   - ManualEntryModal pour saisie manuelle d'entrée carnet
 *   - Capture caméra arrière via input[type=file][capture=environment]
 *   - Conversion base64 sans prefix + envoi à api.parc.scanFacture
 *   - OCR Claude Vision : pré-remplissage formulaire avec date/fournisseur/montantTtc/kmReleve/typesDetectes/lignes/confiance
 */

import { ServiceTechniqueNav } from '@/components/ServiceTechniqueNav';
import { useEffect, useState, useRef, type FormEvent, type ChangeEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { FichePhotos } from '@/components/FichePhotos';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';     // R13 — remplace window.confirm
import {
  Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Truck, Car, ArrowLeft, Plus, Save, Trash2, Camera, X,
  CircleDot, Droplets, Hammer, Wrench, ShieldCheck, Fuel,
  AlertTriangle, Check, FileText, ScanLine, Landmark, AlertCircle,
  Search, FileSpreadsheet,
} from 'lucide-react';
import type {
  VehiculeWithKpis, VehiculeDetail, VehiculeEntreeCarnet,
  TypeIntervention, VehiculeType, OcrFactureResult,
} from '@roronoa-collectivites/shared-types';

// =============================================================================
// Helpers
// =============================================================================

const VEHICULE_TYPE_LABEL: Record<VehiculeType, string> = {
  utilitaire: 'Utilitaire',
  voiture:    'Voiture',
  tracteur:   'Tracteur',
  benne:      'Benne / camion',
  bus:        'Bus / car',
  velo:       'Vélo',
  autre:      'Autre',
};

const TYPE_INTER_LABEL: Record<TypeIntervention, string> = {
  vidange:            'Vidange',
  pneus:              'Pneus',
  freins:             'Freins',
  carrosserie:        'Carrosserie',
  controle_technique: 'Contrôle technique',
  revision:           'Révision',
  reparation:         'Réparation',
  plein_carburant:    'Plein carburant',
  autre:              'Autre',
};

const TYPE_INTER_ICON: Record<TypeIntervention, typeof Wrench> = {
  vidange:            Droplets,
  pneus:              CircleDot,
  freins:             AlertTriangle,
  carrosserie:        Hammer,
  controle_technique: ShieldCheck,
  revision:           Wrench,
  reparation:         Wrench,
  plein_carburant:    Fuel,
  autre:              FileText,
};

function getVehiculeIcon(type: VehiculeType): typeof Truck {
  return type === 'voiture' ? Car : Truck;
}

function formatEuro(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; }
}

// Topbar background partagé (halo tech)
const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};

const SUBNAV_BG = {
  backgroundImage: 'linear-gradient(180deg, oklch(var(--c-bg-2) / 0.3), oklch(var(--c-bg) / 0.4))',
};

const INPUT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

// =============================================================================
// LAYOUT commun
// =============================================================================

function ParcLayout({
  title, eyebrow = 'Service technique', icon = Truck, children, action,
}: {
  title: string;
  eyebrow?: string;
  icon?: typeof Truck;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { tenant, user } = useAuth();
  return (
    <div className="min-h-screen text-ink">
      {/* Topbar glass */}
      <AppHeader module="Service technique" accent="tech" />

      {/* Sub-nav glass (cohérent service-technique R6.2) */}
      {/* D-09 — barre PARTAGÉE. Ces pages réimplémentaient leur propre barre à
          5 onglets : « États des lieux » et « Sécurité ERP » disparaissaient donc
          dès qu'on venait ici. Une seule source de vérité pour la sous-nav. */}
      <ServiceTechniqueNav active="vehicules" right={action} />

      <main id="main-content" className="mx-auto max-w-[1400px] px-4 py-5 animate-fade-in">
        {/* En-tête de page */}
        <div className="mb-5 flex items-center gap-3">
          <LogoMark icon={icon} accent="tech" size="lg" />
          <div>
            <SectionEyebrow as="div">{eyebrow}</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              {title}
            </h1>
          </div>
        </div>

        {children}
      </main>
    </div>
  );
}

function ParcTab({
  children, active, onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  if (active) {
    return (
      <span className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-tech/30 bg-tech-tint/80 px-3 text-xs font-medium text-tech-fg backdrop-blur-glass">
        {children}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1 rounded-r-pill border border-transparent bg-transparent px-3 text-xs font-medium text-ink-muted backdrop-blur-glass transition-all hover:border-border hover:bg-surface/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </button>
  );
}

// =============================================================================
// DASHBOARD PARC (desktop)
// =============================================================================

export function ParcDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.parc.dashboard>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState<'excel' | 'pdf' | null>(null);

  const canCreate = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  async function reload() {
    setLoading(true); setError(null);
    try { setData(await api.parc.dashboard()); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); }, []);

  async function doExport(kind: 'excel' | 'pdf') {
    setExporting(kind); setError(null);
    try {
      if (kind === 'excel') await api.parc.exportExcel();
      else await api.parc.exportPdf();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Export impossible');
    } finally { setExporting(null); }
  }

  const filtered = data?.vehicules.filter((v) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return v.marque.toLowerCase().includes(s)
        || v.modele.toLowerCase().includes(s)
        || (v.immatriculation?.toLowerCase().includes(s) ?? false);
  }) ?? [];

  return (
    <ParcLayout
      title="Parc véhicules"
      icon={Truck}
      action={(
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="secondary" size="sm"
            onClick={() => void doExport('excel')}
            disabled={exporting !== null}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">
              {exporting === 'excel' ? 'Export…' : 'Export Excel'}
            </span>
          </Button>
          <Button
            variant="secondary" size="sm"
            onClick={() => void doExport('pdf')}
            disabled={exporting !== null}
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">
              {exporting === 'pdf' ? 'Export…' : 'Export PDF'}
            </span>
          </Button>
          {canCreate && (
            <Button variant="primary" size="sm" onClick={() => navigate('/parc/vehicules/nouveau')}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Ajouter un véhicule</span>
            </Button>
          )}
        </div>
      )}
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}

      {loading || !data ? (
        <Card variant="glass" padded>
          <div className="py-8 text-center">
            <div
              className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent"
              role="status"
              aria-label="Chargement"
            />
            <p className="mt-4 text-sm text-ink-muted">Chargement du parc…</p>
          </div>
        </Card>
      ) : (
        <>
          {/* KPIs */}
          <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <ParcKpi
              label="Dépenses 12 mois"
              value={formatEuro(data.kpis.depensesAnnee)}
              tone="neutral"
            />
            <ParcKpi
              label="CT à venir < 30j"
              value={String(data.kpis.ctAVenir30j)}
              tone={data.kpis.ctAVenir30j > 0 ? 'warning' : 'neutral'}
            />
            <ParcKpi
              label="Pneus à remplacer"
              value={String(data.kpis.pneusARemplacer)}
              tone={data.kpis.pneusARemplacer > 0 ? 'urgent' : 'neutral'}
            />
            <ParcKpi
              label="Total parc"
              value={String(data.kpis.totalVehicules)}
              tone="success"
              sub={data.kpis.kmMoyensAn !== null ? `${data.kpis.kmMoyensAn.toLocaleString('fr-FR')} km/an moy.` : undefined}
            />
          </div>

          {/* Recherche en Card glass */}
          <Card variant="glass" padded className="mb-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-dim" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher (marque, modèle, immatriculation)…"
                className="h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass pl-9 pr-3 text-sm text-ink placeholder:text-ink-dim shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong"
                aria-label="Rechercher un véhicule"
              />
            </div>
          </Card>

          {/* Tableau */}
          {filtered.length === 0 ? (
            <Card variant="glass" padded>
              <div className="py-8 text-center">
                <div className="flex justify-center mb-3">
                  <LogoMark icon={Truck} accent="tech" size="lg" />
                </div>
                <p className="text-sm text-ink-muted">
                  {data.vehicules.length === 0 ? 'Aucun véhicule' : 'Aucun résultat'}
                </p>
                {data.vehicules.length === 0 && canCreate && (
                  <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/parc/vehicules/nouveau')}>
                    <Plus className="h-4 w-4" aria-hidden="true" /> Ajouter le premier véhicule
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            <Card variant="glass" padded={false}>
              {/* ===== Cards stack mobile (< md) — pattern responsive table ===== */}
              <ul className="md:hidden divide-y divide-border" role="list" aria-label="Liste véhicules">
                {filtered.map((v) => (
                  <li key={v.id}>
                    <VehiculeCardMobile v={v} onClick={() => navigate(`/parc/vehicules/${v.id}`)} />
                  </li>
                ))}
              </ul>

              {/* ===== Table desktop (md+) ===== */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
                    <tr>
                      <th className="px-3 py-2 text-left w-12" aria-label="Type" />
                      <th className="px-3 py-2 text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Véhicule</th>
                      <th className="px-3 py-2 text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Immat.</th>
                      <th className="px-3 py-2 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Compteur</th>
                      <th className="px-3 py-2 text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Prochain CT</th>
                      <th className="px-3 py-2 text-right text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium">Coût cumul.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((v) => (
                      <VehiculeRow key={v.id} v={v} onClick={() => navigate(`/parc/vehicules/${v.id}`)} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </ParcLayout>
  );
}

function ParcKpi({
  label, value, tone, sub,
}: {
  label: string;
  value: string;
  tone: 'neutral' | 'warning' | 'urgent' | 'success';
  sub?: string;
}) {
  // Mappings statiques Tailwind purge-safe
  const styles = {
    neutral: {
      border: 'border-border',
      halo: 'none',
    },
    warning: {
      border: 'border-warning/30',
      halo: 'radial-gradient(160px 80px at 0% 0%, oklch(var(--c-warning) / 0.22), transparent 70%)',
    },
    urgent: {
      border: 'border-urgent/30',
      halo: 'radial-gradient(160px 80px at 0% 0%, oklch(var(--c-urgent) / 0.22), transparent 70%)',
    },
    success: {
      border: 'border-success/30',
      halo: 'radial-gradient(160px 80px at 0% 0%, oklch(var(--c-success) / 0.22), transparent 70%)',
    },
  }[tone];

  const labelColor = {
    neutral: 'text-ink-muted',
    warning: 'text-warning',
    urgent: 'text-urgent',
    success: 'text-success',
  }[tone];

  return (
    <div
      className={`rounded-r-xl border ${styles.border} bg-surface/55 backdrop-blur-glass p-3.5 shadow-soft transition-all hover:shadow-glass-hi`}
      style={{
        backgroundImage:
          styles.halo === 'none'
            ? undefined
            : `${styles.halo}, linear-gradient(180deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg-2) / 0.45))`,
      }}
    >
      <div className={`text-2xs uppercase tracking-r-eyebrow ${labelColor}`}>{label}</div>
      <div className="mt-1 font-display text-2xl font-semibold tracking-r-tight tabular-nums text-ink">
        {value}
      </div>
      {sub && <div className="mt-0.5 text-2xs text-ink-subtle">{sub}</div>}
    </div>
  );
}

function VehiculeRow({ v, onClick }: { v: VehiculeWithKpis; onClick: () => void }) {
  const Icon = getVehiculeIcon(v.type);
  // Halo coloré row si alerte (pneus prioritaires)
  const rowBg = v.alertPneus
    ? 'bg-urgent/5 hover:bg-urgent/10'
    : v.alertCt
    ? 'bg-warning/5 hover:bg-warning/10'
    : 'hover:bg-surface/60';

  return (
    <tr
      className={`cursor-pointer border-b border-border last:border-b-0 transition-colors ${rowBg} focus-within:outline-none focus-within:ring-2 focus-within:ring-inset focus-within:ring-tech/40`}
      onClick={onClick}
      tabIndex={0}
      role="link"
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
    >
      <td className="px-3 py-2">
        <div className="inline-grid h-8 w-8 place-items-center rounded-r-lg bg-tech/15 text-tech-fg">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="text-sm font-medium text-ink">{v.marque} {v.modele}</div>
        <div className="text-2xs text-ink-muted">
          {VEHICULE_TYPE_LABEL[v.type]}
          {v.alertPneus && <span className="ml-2 text-urgent">⚠️ pneus dépassés</span>}
          {v.alertCt && !v.alertPneus && <span className="ml-2 text-warning">⚠️ CT dans {v.joursAvantCt}j</span>}
        </div>
      </td>
      <td className="px-3 py-2 font-mono text-xs text-ink-muted">{v.immatriculation ?? '—'}</td>
      <td className="px-3 py-2 text-right text-sm font-medium text-ink tabular-nums">
        {v.kmActuels !== null ? `${v.kmActuels.toLocaleString('fr-FR')} ${v.uniteCompteur}` : '—'}
      </td>
      <td className="px-3 py-2 text-xs text-ink-muted">{formatDate(v.prochainCt)}</td>
      <td className="px-3 py-2 text-right text-sm font-medium text-ink tabular-nums">{formatEuro(v.coutCumule)}</td>
    </tr>
  );
}

// =============================================================================
// VEHICULE CARD MOBILE — Pattern responsive table L13.6
// =============================================================================

function VehiculeCardMobile({ v, onClick }: { v: VehiculeWithKpis; onClick: () => void }) {
  const Icon = getVehiculeIcon(v.type);
  const cardBg = v.alertPneus
    ? 'bg-urgent/5 border-l-4 border-l-urgent'
    : v.alertCt
    ? 'bg-warning/5 border-l-4 border-l-warning'
    : '';

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'w-full text-left p-3 transition-colors',
        'hover:bg-surface/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tech/40',
        cardBg,
      ].join(' ')}
    >
      {/* Ligne 1 : icône + nom + immat */}
      <div className="flex items-start gap-3">
        <div className="inline-grid h-10 w-10 flex-shrink-0 place-items-center rounded-r-lg bg-tech/15 text-tech-fg">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium text-ink text-sm leading-tight">{v.marque} {v.modele}</div>
          <div className="mt-0.5 flex items-center gap-2 flex-wrap text-2xs text-ink-muted">
            <span>{VEHICULE_TYPE_LABEL[v.type]}</span>
            {v.immatriculation && (
              <>
                <span aria-hidden="true">·</span>
                <span className="font-mono">{v.immatriculation}</span>
              </>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs font-medium text-ink tabular-nums">
            {v.kmActuels !== null ? `${v.kmActuels.toLocaleString('fr-FR')} ${v.uniteCompteur}` : '—'}
          </div>
          <div className="text-2xs text-ink-subtle tabular-nums">{formatEuro(v.coutCumule)}</div>
        </div>
      </div>

      {/* Ligne 2 : alertes + prochain CT */}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap text-2xs">
          {v.alertPneus && (
            <Badge variant="urgent" size="sm">⚠️ pneus dépassés</Badge>
          )}
          {v.alertCt && !v.alertPneus && (
            <Badge variant="warning" size="sm">⚠️ CT dans {v.joursAvantCt}j</Badge>
          )}
        </div>
        {v.prochainCt && (
          <span className="text-2xs text-ink-muted">
            CT&nbsp;: {formatDate(v.prochainCt)}
          </span>
        )}
      </div>
    </button>
  );
}

// =============================================================================
// FICHE VÉHICULE + CARNET (desktop)
// =============================================================================

export function VehiculeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { confirm, dialogElement } = useConfirmDialog();   // R13
  const [data, setData] = useState<VehiculeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddEntry, setShowAddEntry] = useState(false);

  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');
  const canDelete = ['super_admin', 'admin'].includes(user?.role ?? '');

  async function reload() {
    if (!id) return;
    setLoading(true); setError(null);
    try { setData(await api.parc.get(id)); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); /* eslint-disable-next-line */ }, [id]);

  async function doDelete() {
    if (!data) return;
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Supprimer ce véhicule ?',
      description: 'Tout le carnet d\'entretien sera perdu. Cette action est irréversible.',
      confirmLabel: 'Supprimer définitivement',
      danger: true,
    });
    if (!ok) return;
    try { await api.parc.delete(data.id); navigate('/parc'); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
  }

  async function deleteEntry(eid: string) {
    // R13 — Dialog R2 au lieu de window.confirm
    const ok = await confirm({
      title: 'Supprimer cette entrée ?',
      description: 'L\'entrée du carnet d\'entretien sera supprimée.',
      confirmLabel: 'Supprimer',
      danger: true,
    });
    if (!ok) return;
    try { await api.parc.carnet.delete(eid); await reload(); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
  }

  if (loading || !data) {
    return (
      <ParcLayout title="Fiche véhicule" icon={Truck}>
        {error ? (
          <Alert variant="error" icon={AlertCircle}>{error}</Alert>
        ) : (
          <Card variant="glass" padded>
            <div className="py-8 text-center">
              <div
                className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent"
                role="status"
                aria-label="Chargement"
              />
              <p className="mt-4 text-sm text-ink-muted">Chargement du véhicule…</p>
            </div>
          </Card>
        )}
      </ParcLayout>
    );
  }

  const Icon = getVehiculeIcon(data.type);

  return (
    <ParcLayout
      title={`${data.marque} ${data.modele}`}
      icon={Icon}
      action={
        <div className="flex items-center gap-2">
          <Link
            to="/parc"
            className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Parc
          </Link>
          <Link to={`/parc/vehicules/${data.id}/scan`}>
            <Button type="button" variant="primary" size="sm">
              <Camera className="h-3.5 w-3.5" aria-hidden="true" /> Scanner facture
            </Button>
          </Link>
        </div>
      }
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="space-y-3">
          {/* Carte identité */}
          <Card variant="glass" padded>
            <div className="flex items-start gap-3">
              <div
                className="inline-grid h-12 w-12 place-items-center rounded-r-xl bg-tech/15 text-tech-fg flex-shrink-0"
                aria-hidden="true"
              >
                <Icon className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-xl font-semibold tracking-r-snug text-ink">
                  {data.marque} {data.modele}
                </h2>
                <p className="text-xs text-ink-muted">
                  {VEHICULE_TYPE_LABEL[data.type]}
                  {data.immatriculation && <> · <span className="font-mono">{data.immatriculation}</span></>}
                  {data.energie && <> · {data.energie}</>}
                  {data.serviceAffecte && <> · {data.serviceAffecte}</>}
                </p>
              </div>
              <div className="flex flex-col gap-1 items-end flex-shrink-0">
                {data.alertPneus && <Badge variant="urgent" size="sm">Pneus à remplacer</Badge>}
                {data.alertCt && <Badge variant="warning" size="sm">CT à {data.joursAvantCt}j</Badge>}
                {data.alertVidange && <Badge variant="warning" size="sm">Vidange due</Badge>}
                {data.alertAssurance && <Badge variant="warning" size="sm">Assurance a renouveler</Badge>}
              </div>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
              <Stat
                label="Compteur"
                value={data.kmActuels !== null ? `${data.kmActuels.toLocaleString('fr-FR')} ${data.uniteCompteur}` : '—'}
              />
              <Stat label="Mise en service" value={formatDate(data.dateMiseService)} />
              <Stat label="Dernier CT" value={formatDate(data.dernierCt)} />
              <Stat label="Prochain CT" value={formatDate(data.prochainCt)} />
              {data.assuranceEcheance && (
                <Stat
                  label="Assurance"
                  value={data.assureur ? `${formatDate(data.assuranceEcheance)} · ${data.assureur}` : formatDate(data.assuranceEcheance)}
                />
              )}
              <Stat label="Coût cumulé" value={formatEuro(data.coutCumule)} />
              {data.prochaineVidangeKm !== null && (
                <Stat label="Prochaine vidange" value={`${data.prochaineVidangeKm.toLocaleString('fr-FR')} km`} />
              )}
              {data.pneusSeuilAlerteKm !== null && (
                <Stat label="Seuil pneus" value={`${data.pneusSeuilAlerteKm.toLocaleString('fr-FR')} km`} />
              )}
            </dl>

            {data.notes && (
              <div className="mt-4 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3 text-xs text-ink leading-relaxed">
                {data.notes}
              </div>
            )}
          </Card>

          {/* Carnet d'entretien */}
          <Card variant="glass" padded>
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <SectionEyebrow as="h2">Carnet d'entretien ({data.carnet.length})</SectionEyebrow>
              {canEdit && (
                <Button type="button" variant="secondary" size="sm" onClick={() => setShowAddEntry(true)}>
                  <Plus className="h-4 w-4" aria-hidden="true" /> Saisie manuelle
                </Button>
              )}
            </div>

            {data.carnet.length === 0 ? (
              <div className="rounded-r-lg border border-dashed border-border bg-surface/30 backdrop-blur-glass px-4 py-6 text-center">
                <FileText className="mx-auto mb-2 h-8 w-8 text-ink-dim" aria-hidden="true" />
                <p className="text-sm text-ink-muted">Aucune entrée au carnet</p>
                <Link
                  to={`/parc/vehicules/${data.id}/scan`}
                  className="mt-2 inline-flex items-center gap-1 rounded-r-md px-1 text-xs text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                >
                  <Camera className="h-3 w-3" aria-hidden="true" /> Scanner une facture
                </Link>
              </div>
            ) : (
              <ul className="space-y-2">
                {data.carnet.map((e) => (
                  <CarnetEntry key={e.id} e={e} onDelete={canEdit ? () => void deleteEntry(e.id) : undefined} />
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* Sidebar */}
        <aside className="space-y-3">
          <Card variant="glass" padded>
            <SectionEyebrow as="h2" className="mb-3">Actions</SectionEyebrow>
            <div className="space-y-2">
              <Link to={`/parc/vehicules/${data.id}/scan`}>
                <Button type="button" variant="primary" size="sm" className="w-full">
                  <Camera className="h-4 w-4" aria-hidden="true" /> Scanner facture
                </Button>
              </Link>
              {canEdit && (
                <Link to={`/parc/vehicules/${data.id}/edit`}>
                  <Button type="button" variant="secondary" size="sm" className="w-full">
                    Modifier les infos
                  </Button>
                </Link>
              )}
            </div>
          </Card>

          {canDelete && (
            <Card variant="glass" padded>
              <Button type="button" variant="danger" size="sm" className="w-full" onClick={() => void doDelete()}>
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer le véhicule
              </Button>
            </Card>
          )}
        </aside>
      </div>

      {showAddEntry && (
        <ManualEntryModal
          vehiculeId={data.id}
          kmActuels={data.kmActuels}
          onClose={() => setShowAddEntry(false)}
          onSaved={async () => { setShowAddEntry(false); await reload(); }}
        />
      )}
      {dialogElement}{/* R13 — Dialog R2 (confirmations suppression) */}
    </ParcLayout>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 font-medium text-ink">{value}</dd>
    </div>
  );
}

function CarnetEntry({ e, onDelete }: { e: VehiculeEntreeCarnet; onDelete?: () => void }) {
  const Icon = TYPE_INTER_ICON[e.typeIntervention];
  return (
    <li className="flex items-start gap-3 rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass p-3 transition-colors hover:bg-surface/60">
      <div
        className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-tech/15 text-tech-fg flex-shrink-0"
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-semibold text-ink">{TYPE_INTER_LABEL[e.typeIntervention]}</span>
          <span className="text-xs text-ink-muted">{formatDate(e.dateIntervention)}</span>
          {e.source === 'ocr_claude' && (
            <Badge variant="success" size="sm">
              <ScanLine className="h-2.5 w-2.5" aria-hidden="true" /> OCR
            </Badge>
          )}
        </div>
        <div className="mt-0.5 text-xs text-ink-muted">
          {e.fournisseur && <>{e.fournisseur} · </>}
          {e.kmAuMoment !== null && <>au {e.kmAuMoment.toLocaleString('fr-FR')} km · </>}
          <strong className="text-ink">{formatEuro(e.montantTtc)}</strong>
        </div>
        {e.lignes.length > 0 && (
          <ul className="mt-1 text-2xs text-ink-subtle">
            {e.lignes.slice(0, 3).map((l, i) => (
              <li key={i}>· {l.libelle} {l.total !== undefined && `(${formatEuro(l.total)})`}</li>
            ))}
            {e.lignes.length > 3 && <li>· … +{e.lignes.length - 3} autres</li>}
          </ul>
        )}
        {e.notes && <p className="mt-1 text-2xs italic text-ink-muted">{e.notes}</p>}
      </div>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="rounded-r-md p-1.5 text-urgent transition-colors hover:bg-urgent/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-urgent focus-visible:ring-offset-2 focus-visible:ring-offset-bg flex-shrink-0"
          aria-label="Supprimer cette entrée"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

function ManualEntryModal({
  vehiculeId, kmActuels, onClose, onSaved,
}: {
  vehiculeId: string;
  kmActuels: number | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [dateIntervention, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [typeIntervention, setType] = useState<TypeIntervention>('vidange');
  const [kmAuMoment, setKm] = useState<string>(kmActuels?.toString() ?? '');
  const [fournisseur, setFournisseur] = useState('');
  const [montantTtc, setMontant] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setErr(null);
    try {
      await api.parc.carnet.create(vehiculeId, {
        dateIntervention,
        typeIntervention,
        kmAuMoment: kmAuMoment ? parseInt(kmAuMoment, 10) : undefined,
        fournisseur: fournisseur.trim() || undefined,
        montantTtc: montantTtc ? parseFloat(montantTtc) : undefined,
        notes: notes.trim() || undefined,
        source: 'manuel',
      });
      await onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-md p-4"
        >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <Card variant="glass" padded>
          <div className="mb-3 flex items-start justify-between gap-2">
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              Saisie manuelle
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Field label="Date *" htmlFor="me-d">
                <input
                  id="me-d"
                  type="date"
                  value={dateIntervention}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Type *" htmlFor="me-t">
                <select
                  id="me-t"
                  value={typeIntervention}
                  onChange={(e) => setType(e.target.value as TypeIntervention)}
                  className={INPUT_CLS}
                >
                  {Object.entries(TYPE_INTER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Km au moment" htmlFor="me-km">
                <input
                  id="me-km"
                  type="number"
                  min={0}
                  value={kmAuMoment}
                  onChange={(e) => setKm(e.target.value)}
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Montant TTC (€)" htmlFor="me-m">
                <input
                  id="me-m"
                  type="number"
                  min={0}
                  step={0.01}
                  value={montantTtc}
                  onChange={(e) => setMontant(e.target.value)}
                  className={INPUT_CLS}
                />
              </Field>
            </div>
            <Field label="Fournisseur" htmlFor="me-f">
              <input
                id="me-f"
                value={fournisseur}
                onChange={(e) => setFournisseur(e.target.value)}
                placeholder="Garage Dupont, Vulco…"
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Notes" htmlFor="me-n">
              <textarea
                id="me-n"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className={TEXTAREA_CLS}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
              <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                <Save className="h-4 w-4" aria-hidden="true" />
                {submitting ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

// =============================================================================
// FORMULAIRE VÉHICULE (création / édition)
// =============================================================================

/** Prochaine vidange suggérée : compteur actuel + 30 000 km. */
function suggestProchaineVidangeKm(km: number): number {
  return km + 30000;
}

/**
 * Prochain controle technique suggere, selon le type (regles FR) :
 *   - voiture / utilitaire leger (<= 3,5 t) : regle « 4-2 » (1er CT a 4 ans,
 *     puis tous les 2 ans) ; identique quelle que soit l'energie.
 *   - bus (transport en commun) : CT annuel.
 *   - benne / tracteur / velo / autre : periodicite trop variable (poids,
 *     usage agricole...) -> pas de pre-remplissage, saisie manuelle.
 */
function suggestProchainCt(dateMiseServiceIso: string, type: VehiculeType): string | null {
  const mec = new Date(dateMiseServiceIso);
  if (Number.isNaN(mec.getTime())) return null;
  let premierAns: number;
  let intervalleAns: number;
  if (type === 'bus') {
    premierAns = 1; intervalleAns = 1;
  } else if (type === 'voiture' || type === 'utilitaire') {
    premierAns = 4; intervalleAns = 2;
  } else {
    return null;
  }
  const d = new Date(mec);
  d.setFullYear(d.getFullYear() + premierAns);
  const today = new Date();
  while (d.getTime() <= today.getTime()) {
    d.setFullYear(d.getFullYear() + intervalleAns);
  }
  return d.toISOString().slice(0, 10);
}

export function VehiculeFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState<{
    type: VehiculeType;
    marque: string;
    modele: string;
    immatriculation: string;
    energie: string;
    dateMiseService: string;
    kmActuels: string;
    uniteCompteur: 'km' | 'h';
    prochainCt: string;
    dernierCt: string;
    prochaineVidangeKm: string;
    pneusSeuilAlerteKm: string;
    assuranceEcheance: string;
    assureur: string;
    serviceAffecte: string;
    notes: string;
  }>({
    type: 'utilitaire', marque: '', modele: '', immatriculation: '', energie: '',
    dateMiseService: '', kmActuels: '', uniteCompteur: 'km',
    prochainCt: '', dernierCt: '', prochaineVidangeKm: '', pneusSeuilAlerteKm: '',
    assuranceEcheance: '', assureur: '',
    serviceAffecte: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pre-remplissage CT/vidange : actif tant que l'utilisateur n'a pas saisi le champ a la main.
  const [ctTouched, setCtTouched] = useState(false);
  const [vidangeTouched, setVidangeTouched] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;
    void api.parc.get(id).then((v) => {
      setForm({
        type: v.type, marque: v.marque, modele: v.modele,
        immatriculation: v.immatriculation ?? '', energie: v.energie ?? '',
        dateMiseService: v.dateMiseService ?? '',
        kmActuels: v.kmActuels?.toString() ?? '', uniteCompteur: v.uniteCompteur,
        dernierCt: v.dernierCt ?? '', prochainCt: v.prochainCt ?? '',
        prochaineVidangeKm: v.prochaineVidangeKm?.toString() ?? '',
        pneusSeuilAlerteKm: v.pneusSeuilAlerteKm?.toString() ?? '',
        assuranceEcheance: v.assuranceEcheance ?? '', assureur: v.assureur ?? '',
        serviceAffecte: v.serviceAffecte ?? '', notes: v.notes ?? '',
      });
      // En edition : valeurs deja presentes => on ne les recalcule pas.
      setCtTouched(!!v.prochainCt);
      setVidangeTouched(!!v.prochaineVidangeKm);
    }).catch((e) => setError(e instanceof ApiException ? e.payload.error : 'Erreur'));
  }, [id, isEdit]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setError(null);
    const body: Record<string, unknown> = {
      type: form.type,
      marque: form.marque.trim(),
      modele: form.modele.trim(),
      uniteCompteur: form.uniteCompteur,
    };
    if (form.immatriculation.trim()) body.immatriculation = form.immatriculation.trim();
    if (form.energie) body.energie = form.energie;
    if (form.dateMiseService) body.dateMiseService = form.dateMiseService;
    if (form.kmActuels) body.kmActuels = parseInt(form.kmActuels, 10);
    if (form.dernierCt) body.dernierCt = form.dernierCt;
    if (form.prochainCt) body.prochainCt = form.prochainCt;
    if (form.prochaineVidangeKm) body.prochaineVidangeKm = parseInt(form.prochaineVidangeKm, 10);
    if (form.pneusSeuilAlerteKm) body.pneusSeuilAlerteKm = parseInt(form.pneusSeuilAlerteKm, 10);
    if (form.serviceAffecte.trim()) body.serviceAffecte = form.serviceAffecte.trim();
    if (form.notes.trim()) body.notes = form.notes.trim();
    if (form.assuranceEcheance) body.assuranceEcheance = form.assuranceEcheance;
    if (form.assureur.trim()) body.assureur = form.assureur.trim();

    try {
      const res = isEdit && id
        ? await api.parc.update(id, body)
        : await api.parc.create(body);
      navigate(`/parc/vehicules/${res.id}`);
    } catch (e2) {
      setError(e2 instanceof ApiException ? e2.payload.error : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <ParcLayout
      title={isEdit ? 'Modifier véhicule' : 'Nouveau véhicule'}
      icon={Truck}
      action={
        <Link
          to="/parc"
          className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-tech-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Parc
        </Link>
      }
    >
      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-3">
          {error}
        </Alert>
      )}
      <Card variant="glass" padded>
        <form onSubmit={onSubmit} className="space-y-3 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type *" htmlFor="vf-type">
              <select
                id="vf-type"
                value={form.type}
                onChange={(e) => {
                  const newType = e.target.value as VehiculeType;
                  setForm((f) => {
                    const next = { ...f, type: newType };
                    if (!ctTouched && f.dateMiseService) {
                      next.prochainCt = suggestProchainCt(f.dateMiseService, newType) ?? '';
                    }
                    return next;
                  });
                }}
                className={INPUT_CLS}
              >
                {Object.entries(VEHICULE_TYPE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Énergie" htmlFor="vf-energie">
              <select
                id="vf-energie"
                value={form.energie}
                onChange={(e) => setForm({ ...form, energie: e.target.value })}
                className={INPUT_CLS}
              >
                <option value="">—</option>
                <option value="diesel">Diesel</option>
                <option value="essence">Essence</option>
                <option value="electrique">Électrique</option>
                <option value="hybride">Hybride</option>
                <option value="gnv">GNV</option>
                <option value="autre">Autre</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Marque *" htmlFor="vf-marque">
              <input
                id="vf-marque"
                value={form.marque}
                onChange={(e) => setForm({ ...form, marque: e.target.value })}
                required minLength={2} maxLength={50}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Modèle *" htmlFor="vf-modele">
              <input
                id="vf-modele"
                value={form.modele}
                onChange={(e) => setForm({ ...form, modele: e.target.value })}
                required minLength={1} maxLength={100}
                className={INPUT_CLS}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Immatriculation" htmlFor="vf-immat">
              <input
                id="vf-immat"
                value={form.immatriculation}
                onChange={(e) => setForm({ ...form, immatriculation: e.target.value })}
                placeholder="AB-123-CD" maxLength={20}
                className={`${INPUT_CLS} font-mono`}
              />
            </Field>
            <Field label="Mise en service" htmlFor="vf-dms">
              <input
                id="vf-dms"
                type="date"
                value={form.dateMiseService}
                onChange={(e) => {
                  const dms = e.target.value;
                  setForm((f) => {
                    const next = { ...f, dateMiseService: dms };
                    if (!ctTouched) {
                      next.prochainCt = dms ? (suggestProchainCt(dms, f.type) ?? '') : '';
                    }
                    return next;
                  });
                }}
                className={INPUT_CLS}
              />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Compteur" htmlFor="vf-km">
              <input
                id="vf-km"
                type="number" min={0}
                value={form.kmActuels}
                onChange={(e) => {
                  const kmStr = e.target.value;
                  setForm((f) => {
                    const next = { ...f, kmActuels: kmStr };
                    if (!vidangeTouched) {
                      const km = parseInt(kmStr, 10);
                      next.prochaineVidangeKm = (kmStr && !Number.isNaN(km))
                        ? String(suggestProchaineVidangeKm(km)) : '';
                    }
                    return next;
                  });
                }}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Unité" htmlFor="vf-unit">
              <select
                id="vf-unit"
                value={form.uniteCompteur}
                onChange={(e) => setForm({ ...form, uniteCompteur: e.target.value as 'km' | 'h' })}
                className={INPUT_CLS}
              >
                <option value="km">km</option>
                <option value="h">heures</option>
              </select>
            </Field>
            <Field label="Service" htmlFor="vf-serv">
              <input
                id="vf-serv"
                value={form.serviceAffecte}
                onChange={(e) => setForm({ ...form, serviceAffecte: e.target.value })}
                placeholder="Service technique" maxLength={50}
                className={INPUT_CLS}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dernier CT" htmlFor="vf-dct">
              <input
                id="vf-dct"
                type="date"
                value={form.dernierCt}
                onChange={(e) => setForm({ ...form, dernierCt: e.target.value })}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Prochain CT" htmlFor="vf-pct">
              <input
                id="vf-pct"
                type="date"
                value={form.prochainCt}
                onChange={(e) => { setCtTouched(true); setForm((f) => ({ ...f, prochainCt: e.target.value })); }}
                className={INPUT_CLS}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Prochaine vidange (km)" htmlFor="vf-pv">
              <input
                id="vf-pv"
                type="number" min={0}
                value={form.prochaineVidangeKm}
                onChange={(e) => { setVidangeTouched(true); setForm((f) => ({ ...f, prochaineVidangeKm: e.target.value })); }}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Seuil alerte pneus (km)" htmlFor="vf-sap">
              <input
                id="vf-sap"
                type="number" min={0}
                value={form.pneusSeuilAlerteKm}
                onChange={(e) => setForm({ ...form, pneusSeuilAlerteKm: e.target.value })}
                className={INPUT_CLS}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Echeance assurance" htmlFor="vf-assech">
              <input
                id="vf-assech"
                type="date"
                value={form.assuranceEcheance}
                onChange={(e) => setForm({ ...form, assuranceEcheance: e.target.value })}
                className={INPUT_CLS}
              />
            </Field>
            <Field label="Assureur" htmlFor="vf-assr">
              <input
                id="vf-assr"
                value={form.assureur}
                onChange={(e) => setForm({ ...form, assureur: e.target.value })}
                placeholder="Groupama, MAIF..." maxLength={100}
                className={INPUT_CLS}
              />
            </Field>
          </div>
          <Field label="Notes" htmlFor="vf-notes">
            <textarea
              id="vf-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2} maxLength={5000}
              className={TEXTAREA_CLS}
            />
          </Field>

          {/* Photos du véhicule (2 max, internes) — item 3.10. Disponible une
              fois la fiche créée : l'upload a besoin de son id. */}
          {isEdit && id && (
            <FichePhotos
              ficheId={id}
              api={api.parc.photos}
              titre="Photos du véhicule"
            />
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/parc')}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" size="sm" disabled={submitting}>
              <Save className="h-4 w-4" aria-hidden="true" />
              {submitting ? 'Enregistrement…' : isEdit ? 'Enregistrer' : 'Créer'}
            </Button>
          </div>
        </form>
      </Card>
    </ParcLayout>
  );
}

// =============================================================================
// SCAN FACTURE (mobile-first PWA)
// =============================================================================

export function ScanFactureMobilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [vehicule, setVehicule] = useState<VehiculeDetail | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image/jpeg' | 'image/png' | 'image/webp'>('image/jpeg');
  const [ocrStatus, setOcrStatus] = useState<'idle' | 'scanning' | 'done' | 'error'>('idle');
  const [ocrResult, setOcrResult] = useState<OcrFactureResult | null>(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form (peut être édité après OCR)
  const [date, setDate] = useState('');
  const [fournisseur, setFournisseur] = useState('');
  const [montantTtc, setMontantTtc] = useState('');
  const [kmReleve, setKmReleve] = useState('');
  const [typeIntervention, setTypeIntervention] = useState<TypeIntervention>('vidange');

  useEffect(() => {
    if (!id) return;
    void api.parc.get(id).then(setVehicule).catch(() => navigate('/parc'));
  }, [id, navigate]);

  async function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    if (!raw) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(raw.type)) {
      setError('Format non supporté (JPEG, PNG ou WebP requis)');
      return;
    }
    if (raw.size > 10 * 1024 * 1024) {
      setError('Image trop volumineuse (max 10 Mo)');
      return;
    }
    setError(null);
    // Compression côté client avant lecture base64 (limite l'upload OCR).
    const f = await compressImageIfNeeded(raw);
    setMediaType(f.type as 'image/jpeg' | 'image/png' | 'image/webp');
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      setImagePreview(result);
      // Garder le base64 pur sans prefix
      const base64 = result.split(',')[1] ?? '';
      setImageBase64(base64);
    };
    reader.readAsDataURL(f);
  }

  async function doScan() {
    if (!imageBase64 || !id) return;
    setOcrStatus('scanning'); setError(null);
    try {
      const result = await api.parc.scanFacture(id, imageBase64, mediaType);
      setOcrResult(result);
      // Pré-remplir le formulaire
      if (result.date) setDate(result.date);
      if (result.fournisseur) setFournisseur(result.fournisseur);
      if (result.montantTtc) setMontantTtc(String(result.montantTtc));
      if (result.kmReleve) setKmReleve(String(result.kmReleve));
      if (result.typesDetectes.length > 0) setTypeIntervention(result.typesDetectes[0]!);
      setOcrStatus('done');
    } catch (err) {
      setOcrStatus('error');
      setError(err instanceof ApiException ? err.payload.error : 'Erreur OCR');
    }
  }

  async function doValidate() {
    if (!id) return;
    setValidating(true); setError(null);
    try {
      await api.parc.carnet.create(id, {
        dateIntervention: date || new Date().toISOString().slice(0, 10),
        typeIntervention,
        kmAuMoment: kmReleve ? parseInt(kmReleve, 10) : undefined,
        fournisseur: fournisseur.trim() || undefined,
        montantTtc: montantTtc ? parseFloat(montantTtc) : undefined,
        montantHt: ocrResult?.montantHt ?? undefined,
        lignes: ocrResult?.lignes,
        source: ocrResult ? 'ocr_claude' : 'manuel',
        ocrConfiance: ocrResult?.confiance,
      });
      navigate(`/parc/vehicules/${id}`);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur enregistrement');
      setValidating(false);
    }
  }

  if (!vehicule) {
    return (
      <div className="flex min-h-screen items-center justify-center text-ink">
        <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-tech border-t-transparent" role="status" />
      </div>
    );
  }

  const Icon = getVehiculeIcon(vehicule.type);

  return (
    <div className="min-h-screen text-ink pb-6">
      {/* Header mobile compact glass */}
      <header
        className="sticky top-0 z-20 border-b border-border backdrop-blur-glass"
        style={TOPBAR_BG}
      >
        <div className="mx-auto flex max-w-md items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate(`/parc/vehicules/${id}`)}
            aria-label="Retour"
            className="inline-flex h-10 w-10 items-center justify-center rounded-r-lg border border-border bg-surface/60 text-ink-muted backdrop-blur-glass transition-all hover:bg-surface/80 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <LogoMark icon={Icon} accent="tech" size="md" />
          <div className="flex-1 min-w-0">
            <SectionEyebrow as="div">Nouvelle facture</SectionEyebrow>
            <div className="font-display text-sm font-semibold tracking-r-snug text-ink truncate">
              {vehicule.marque} {vehicule.modele}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-md space-y-3 p-3 animate-fade-in">
        {error && (
          <Alert variant="error" icon={AlertCircle}>{error}</Alert>
        )}

        {/* Véhicule sélectionné (rappel) */}
        <Card variant="glass" padded>
          <div className="flex items-center gap-3">
            <div
              className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-tech/15 text-tech-fg flex-shrink-0"
              aria-hidden="true"
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-ink">{vehicule.marque} {vehicule.modele}</div>
              {vehicule.immatriculation && (
                <div className="font-mono text-2xs text-ink-muted">{vehicule.immatriculation}</div>
              )}
            </div>
          </div>
        </Card>

        {/* Capture photo */}
        <Card variant="glass" padded>
          <SectionEyebrow as="h2" className="mb-2">Facture</SectionEyebrow>

          {imagePreview ? (
            <div className="space-y-2">
              <div className="relative overflow-hidden rounded-r-lg border border-border bg-surface/40">
                <img src={imagePreview} alt="Facture scannée" className="block w-full" />
                {ocrStatus === 'done' && (
                  <div className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-r-pill bg-gradient-success px-2 py-0.5 text-2xs font-medium text-white shadow-glow-success">
                    <Check className="h-3 w-3" aria-hidden="true" /> OCR OK
                  </div>
                )}
                {ocrStatus === 'scanning' && (
                  <div className="absolute inset-0 flex items-center justify-center bg-ink/60 backdrop-blur-md">
                    <div className="inline-flex items-center gap-2 rounded-r-pill border border-border bg-surface/95 backdrop-blur-glass px-4 py-2 text-sm font-medium text-ink shadow-pop">
                      <ScanLine className="h-4 w-4 animate-pulse text-tech-fg" aria-hidden="true" />
                      Analyse OCR…
                    </div>
                  </div>
                )}
              </div>
              {ocrStatus === 'idle' && (
                <Button type="button" variant="primary" className="w-full" onClick={() => void doScan()}>
                  <ScanLine className="h-4 w-4" aria-hidden="true" /> Lancer l'analyse OCR
                </Button>
              )}
              {ocrStatus === 'error' && (
                <Button type="button" variant="primary" className="w-full" onClick={() => void doScan()}>
                  <ScanLine className="h-4 w-4" aria-hidden="true" /> Réessayer
                </Button>
              )}
              <button
                type="button"
                onClick={() => {
                  setImagePreview(null);
                  setImageBase64(null);
                  setOcrResult(null);
                  setOcrStatus('idle');
                }}
                className="block w-full rounded-r-lg border border-border bg-surface/40 backdrop-blur-glass py-2 text-center text-xs text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                Reprendre une photo
              </button>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                onChange={handleFile}
                className="sr-only"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex aspect-[7/9] w-full flex-col items-center justify-center gap-2 rounded-r-xl border-2 border-dashed border-border bg-surface/30 backdrop-blur-glass transition-all hover:border-tech/40 hover:bg-surface/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
              >
                <div
                  className="inline-grid h-14 w-14 place-items-center rounded-r-xl bg-tech/15 text-tech-fg"
                  aria-hidden="true"
                >
                  <Camera className="h-7 w-7" />
                </div>
                <span className="font-display text-sm font-semibold tracking-r-snug text-ink">
                  Prendre une photo
                </span>
                <span className="text-2xs text-ink-muted">ou choisir un fichier</span>
              </button>
            </>
          )}
        </Card>

        {/* Champs extraits */}
        {ocrStatus === 'done' && ocrResult && (
          <Card variant="glass" padded>
            <div className="mb-2 flex items-center justify-between gap-2">
              <SectionEyebrow as="h2">Extrait — vérifier puis valider</SectionEyebrow>
              <span className="text-2xs text-ink-subtle tabular-nums">Confiance {ocrResult.confiance}%</span>
            </div>

            <div className="space-y-2">
              <Field label="Date" htmlFor="sc-d">
                <input
                  id="sc-d"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={INPUT_CLS}
                />
              </Field>
              <Field label="Type d'intervention" htmlFor="sc-t">
                <select
                  id="sc-t"
                  value={typeIntervention}
                  onChange={(e) => setTypeIntervention(e.target.value as TypeIntervention)}
                  className={INPUT_CLS}
                >
                  {Object.entries(TYPE_INTER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Km relevé" htmlFor="sc-km">
                  <input
                    id="sc-km"
                    type="number" min={0}
                    value={kmReleve}
                    onChange={(e) => setKmReleve(e.target.value)}
                    className={INPUT_CLS}
                  />
                </Field>
                <Field label="Montant TTC (€)" htmlFor="sc-m">
                  <input
                    id="sc-m"
                    type="number" min={0} step={0.01}
                    value={montantTtc}
                    onChange={(e) => setMontantTtc(e.target.value)}
                    className={INPUT_CLS}
                  />
                </Field>
              </div>
              <Field label="Fournisseur" htmlFor="sc-f">
                <input
                  id="sc-f"
                  value={fournisseur}
                  onChange={(e) => setFournisseur(e.target.value)}
                  className={INPUT_CLS}
                />
              </Field>

              {ocrResult.typesDetectes.length > 0 && (
                <div className="rounded-r-lg border border-warning/40 bg-warning/10 backdrop-blur-glass px-2.5 py-1.5 text-xs text-ink">
                  <strong className="text-warning">Détecté :</strong>{' '}
                  {ocrResult.typesDetectes.map((t) => TYPE_INTER_LABEL[t]).join(', ')}
                </div>
              )}

              {ocrResult.lignes.length > 0 && (
                <details className="rounded-r-lg border border-border bg-surface/30 backdrop-blur-glass">
                  <summary className="cursor-pointer px-3 py-2 text-xs font-medium text-ink transition-colors hover:bg-surface/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-lg">
                    {ocrResult.lignes.length} ligne{ocrResult.lignes.length > 1 ? 's' : ''} détectée{ocrResult.lignes.length > 1 ? 's' : ''}
                  </summary>
                  <ul className="px-3 pb-2 text-2xs text-ink-muted">
                    {ocrResult.lignes.map((l, i) => (
                      <li key={i}>· {l.libelle} {l.total !== undefined && `(${formatEuro(l.total)})`}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </Card>
        )}

        {/* Bouton valider */}
        {(ocrStatus === 'done' || imagePreview) && (
          <Button
            type="button"
            variant="primary"
            className="w-full"
            disabled={validating || !date}
            onClick={() => void doValidate()}
          >
            <Check className="h-4 w-4" aria-hidden="true" />
            {validating ? 'Enregistrement…' : 'Valider et enregistrer'}
          </Button>
        )}
      </main>
    </div>
  );
}
